"use client";

import { parseUsdLabel, PUBLIC_CURRENCY_SELECTOR_ENABLED } from "@/greenpak/display-currency";
import { useDisplayCurrency } from "@/components/chrome/CurrencyProvider";

type DisplayPriceProps = {
  amountUsd?: number;
  /** Exact PKR figure (e.g. from Room.basePricePKR). Shown as-is when the
   *  traveller's display currency is PKR, so quoted rates always match what
   *  the reservations team actually charges — no round-trip through USD. */
  pricePkr?: number;
  /** Bypasses all currency conversion — rendered exactly as given. For
   *  package/contact-only copy ("Contact us for rates", "Packages from
   *  PKR 17,000 + tax") that must never be reinterpreted as a USD amount. */
  literal?: string;
  fromLabel?: string;
  prefix?: string;
  suffix?: string;
  className?: string;
  bold?: boolean;
};

export function DisplayPrice({
  amountUsd,
  pricePkr,
  literal,
  fromLabel,
  prefix,
  suffix,
  className,
  bold = false,
}: DisplayPriceProps) {
  const { currency, formatUsd, formatMoney } = useDisplayCurrency();

  if (literal != null) {
    return (
      <span className={className}>
        {bold ? <b className="text-[var(--foreground)]">{literal}</b> : literal}
      </span>
    );
  }

  // Canonical PKR path — the exact quoted rate, formatted through the same
  // shared formatter every other currency uses (never a hand-rolled string),
  // so PKR 217,000 here is byte-identical to PKR 217,000 anywhere else on
  // the site showing the same underlying amount.
  // 0 means the price is given on request (a tour with no published rate),
  // never "PKR 0": no prefix or suffix, which would read "From … pp".
  if (pricePkr === 0) {
    return (
      <span className={className}>
        {bold ? <b className="text-[var(--foreground)]">Price on request</b> : "Price on request"}
      </span>
    );
  }

  if (pricePkr != null && currency === "PKR") {
    const formatted = formatMoney(pricePkr, "PKR");
    if (bold) {
      return (
        <span className={className}>
          {prefix ? `${prefix} ` : null}
          <b className="text-[var(--foreground)]">{formatted}</b>
          {suffix ? ` ${suffix}` : null}
        </span>
      );
    }
    return (
      <span className={className}>
        {prefix ? `${prefix} ` : null}
        {formatted}
        {suffix ? ` ${suffix}` : null}
      </span>
    );
  }

  // No canonical pricePkr for this item — in the PKR-only release, that means
  // no verified PKR figure exists, so the price is left blank rather than
  // computed from a USD source through the legacy fixed FX table (release
  // spec Part 3: that table is not an approved public conversion source).
  // Re-enable once every remaining call site carries a real basePricePKR.
  if (!PUBLIC_CURRENCY_SELECTOR_ENABLED) {
    return null;
  }

  const usd = amountUsd ?? (fromLabel ? parseUsdLabel(fromLabel) : null);

  if (usd == null) {
    return fromLabel ? <span className={className}>{fromLabel}</span> : null;
  }

  const text = formatUsd(usd, { prefix, suffix });
  if (bold) {
    const core = formatUsd(usd);
    return (
      <span className={className}>
        {prefix ? `${prefix} ` : null}
        <b className="text-[var(--foreground)]">{core}</b>
        {suffix ? ` ${suffix}` : null}
      </span>
    );
  }

  return <span className={className}>{text}</span>;
}
